<template>
  <main class="flex justify-center bg-gray-50">
    <HeaderComponent />
    <section class="w-full pb-[100px] lg:pl-[50px] lg:pt-[95px] pt-[95px] max-h-screen overflow-y-auto relative flex justify-start flex-col lg:p-[25px] p-[20px] h-auto min-h-screen">

      <div class="w-full mb-4 flex flex-col md:flex-row md:justify-between md:items-center gap-3">
        <div>
          <h1 class="text-2xl font-bold text-gray-800">Fidélisation</h1>
          <p class="text-sm text-gray-500">Meilleurs clients de l'année {{ year }}</p>
        </div>
        <select v-model="year" @change="fetchData" class="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 self-start">
          <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
        </select>
      </div>

      <!-- Paliers -->
      <div class="grid grid-cols-3 gap-4 mb-6">
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-4 text-center">
          <p class="text-xs text-gray-500 mb-1">≥ 500 000 F</p>
          <p class="text-2xl font-bold text-amber-600">{{ paliers.p500k || 0 }}</p>
        </div>
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-4 text-center">
          <p class="text-xs text-gray-500 mb-1">≥ 1 000 000 F</p>
          <p class="text-2xl font-bold text-orange-600">{{ paliers.p1M || 0 }}</p>
        </div>
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-4 text-center">
          <p class="text-xs text-gray-500 mb-1">≥ 2 000 000 F</p>
          <p class="text-2xl font-bold text-red-600">{{ paliers.p2M || 0 }}</p>
        </div>
      </div>

      <!-- Top 3 par palier -->
      <h2 class="text-lg font-bold text-gray-800 mb-3">Top 3 par palier (cadeau de fin d'année)</h2>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div v-for="p in [{key:'500k',label:'≥ 500 000 F'},{key:'1M',label:'≥ 1 000 000 F'},{key:'2M',label:'≥ 2 000 000 F'}]" :key="p.key" class="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <h3 class="font-bold text-gray-700 mb-3 flex items-center gap-2"><i class="fa-solid fa-trophy text-orange-500"></i> {{ p.label }}</h3>
          <div v-if="!(topParPalier[p.key] && topParPalier[p.key].length)" class="text-xs text-gray-400">Aucune donnée</div>
          <div v-for="(c, i) in (topParPalier[p.key] || [])" :key="i" class="flex items-center gap-2 py-2 border-b border-gray-50 last:border-b-0">
            <span :class="rankClass(i)" class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">{{ i + 1 }}</span>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-800 truncate">{{ c.nom || 'Client' }} {{ c.prenoms || '' }}</p>
              <p class="text-xs text-gray-400">{{ c.numero }}</p>
            </div>
            <span class="text-sm font-bold text-emerald-600">{{ formatMoney(c.total) }}</span>
          </div>
        </div>
      </div>

      <!-- Classement global -->
      <h2 class="text-lg font-bold text-gray-800 mb-3">Classement général</h2>
      <div v-if="isLoading" class="text-center h-[200px] w-full flex flex-col justify-center items-center">
        <div class="loader mb-4"></div>
      </div>
      <div v-else-if="classement.length === 0" class="text-center h-[150px] flex flex-col justify-center items-center">
        <i class="fa-solid fa-users text-4xl text-gray-300 mb-3"></i>
        <p class="text-gray-500">Aucune vente cette année</p>
      </div>
      <div v-else class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        <table class="w-full text-sm min-w-[600px]">
          <thead>
            <tr class="text-left text-gray-500 border-b border-gray-100">
              <th class="px-4 py-3 font-semibold">#</th>
              <th class="px-4 py-3 font-semibold">Client</th>
              <th class="px-4 py-3 font-semibold">Numéro</th>
              <th class="px-4 py-3 font-semibold text-center">Ventes</th>
              <th class="px-4 py-3 font-semibold text-center">Palier</th>
              <th class="px-4 py-3 font-semibold text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(c, i) in classement" :key="c.numero" class="border-b border-gray-50 hover:bg-gray-50">
              <td class="px-4 py-3 text-gray-500">{{ i + 1 }}</td>
              <td class="px-4 py-3 font-medium text-gray-800">{{ c.nom }} {{ c.prenoms }}</td>
              <td class="px-4 py-3 text-gray-500">{{ c.numero }}</td>
              <td class="px-4 py-3 text-center text-gray-600">{{ c.nbVentes }}</td>
              <td class="px-4 py-3 text-center">
                <span v-if="c.palier" :class="palierClass(c.palier)" class="text-[11px] px-2 py-1 rounded-full font-semibold">{{ c.palier }}</span>
                <span v-else class="text-gray-300">—</span>
              </td>
              <td class="px-4 py-3 text-right font-bold text-emerald-600">{{ formatMoney(c.total) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

    </section>
  </main>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import HeaderComponent from '../components/HeaderComponent.vue';
import axios from 'axios';

const nowYear = new Date().getFullYear();
const years = [nowYear, nowYear - 1, nowYear - 2];
const year = ref(nowYear);

const classement = ref([]);
const topParPalier = ref({});
const paliers = ref({ p500k: 0, p1M: 0, p2M: 0 });
const isLoading = ref(true);

const formatMoney = (n) => `${new Intl.NumberFormat('fr-FR').format(Math.round(Number(n) || 0))} F`;
function rankClass(i) { return i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-gray-200 text-gray-600' : 'bg-orange-100 text-orange-700'; }
function palierClass(p) { return p === '2M' ? 'bg-red-100 text-red-700' : p === '1M' ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700'; }

async function fetchData() {
  isLoading.value = true;
  try {
    const { data } = await axios.get('/api/sales/loyalty', { params: { year: year.value } });
    classement.value = data.classement || [];
    topParPalier.value = data.topParPalier || {};
    paliers.value = data.paliers || paliers.value;
  } catch (e) { /* */ }
  finally { isLoading.value = false; }
}

onMounted(fetchData);
</script>

<style scoped>
.loader { border: 3px solid #f3f3f3; border-top: 3px solid #f97316; border-radius: 50%; width: 30px; height: 30px; animation: spin 1s linear infinite; }
@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
</style>
